<script setup>
/**
 * A transaction's files, in its sheet (stores/attachments.js). Like the rest
 * of the sheet, nothing happens until it is saved: picked files wait here, a
 * removed one is only hidden, and `commit(id)` — the sheet's, once the
 * transaction is saved and has its id — attaches and removes them.
 *
 * Files come from the picker, the camera (a coarse pointer), a drop on the
 * section, or a paste anywhere in the sheet. Each is checked when it is picked
 * (images and PDFs, 20 MB). Images show as themselves; a PDF, or an image the
 * browser cannot draw (a phone's HEIC), as its name. Opening one shows it in a
 * tab of its own.
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import Icon from './ui/Icon.vue'
import { errorText, t } from '../stores/i18n'
import { state } from '../stores/state'
import { attach, attachmentsOf, canAttach, detach, fileOf, readPicked, waiting } from '../stores/attachments'
import { showable, sizeLabel } from '../lib/attachments'

const props = defineProps({ transactionId: { type: String, default: null } })

const rows = ref([]) // attached already
const urls = ref({}) // id → an object URL, for those that show
const missing = ref({}) // id → why it cannot be shown: missing | pending | offline
const removed = ref(new Set())
const staged = ref([]) // { key, picked, url }
const error = ref('')
const dragging = ref(false)
const phone = typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches
// A viewer of a cloud ledger sees the files, and changes none.
const editable = canAttach()

const shown = computed(() => [
  ...rows.value.filter((r) => !removed.value.has(r.id)).map((r) => ({ key: r.id, name: r.name, type: r.type, size: r.size, url: urls.value[r.id], row: r })),
  ...staged.value.map((s) => ({ key: s.key, name: s.picked.name, type: s.picked.type, size: s.picked.size, url: s.url, picked: s.picked })),
])

onMounted(async () => {
  document.addEventListener('paste', onPaste)
  if (!props.transactionId) return
  rows.value = await attachmentsOf(props.transactionId)
  for (const row of rows.value) {
    try {
      const blob = await fileOf(row)
      if (showable(row.type)) urls.value = { ...urls.value, [row.id]: URL.createObjectURL(blob) }
    } catch (err) {
      const why = err?.code === 'attachment-pending' ? 'pending' : err?.code === 'attachment-offline' ? 'offline' : 'missing'
      missing.value = { ...missing.value, [row.id]: why }
    }
  }
})
onBeforeUnmount(() => {
  document.removeEventListener('paste', onPaste)
  for (const url of [...Object.values(urls.value), ...staged.value.map((s) => s.url)]) if (url) URL.revokeObjectURL(url)
})

async function pick(list) {
  error.value = ''
  for (const file of list ?? []) {
    try {
      const picked = await readPicked(file)
      const url = showable(picked.type) ? URL.createObjectURL(new Blob([picked.bytes], { type: picked.type })) : null
      staged.value = [...staged.value, { key: `new-${Math.random().toString(36).slice(2)}`, picked, url }]
    } catch (err) {
      error.value = `${file.name}: ${errorText(err)}`
    }
  }
}
const onInput = (event) => {
  pick([...event.target.files])
  event.target.value = ''
}
function onDrop(event) {
  dragging.value = false
  if (!editable) return
  pick([...(event.dataTransfer?.files ?? [])])
}
function onPaste(event) {
  if (!editable) return
  const pasted = [...(event.clipboardData?.files ?? [])]
  if (!pasted.length) return
  event.preventDefault()
  pick(pasted)
}

/** Opens a file in a tab of its own (or, where tabs are refused, downloads it). */
async function open(item) {
  error.value = ''
  try {
    const blob = item.picked ? new Blob([item.picked.bytes], { type: item.type }) : await fileOf(item.row)
    const url = URL.createObjectURL(blob)
    if (!window.open(url, '_blank')) {
      const a = Object.assign(document.createElement('a'), { href: url, download: item.name })
      a.click()
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
  } catch (err) {
    error.value = errorText(err)
  }
}

function drop(item) {
  if (item.row) removed.value = new Set([...removed.value, item.row.id])
  else {
    if (item.url) URL.revokeObjectURL(item.url)
    staged.value = staged.value.filter((s) => s.key !== item.key)
  }
}

/** What the sheet calls once the transaction is saved: the picked files attached, the removed ones gone. */
async function commit(transactionId) {
  for (const s of staged.value) await attach(transactionId, s.picked)
  for (const row of rows.value) if (removed.value.has(row.id)) await detach(row)
}
defineExpose({ commit })
</script>

<template>
  <section
    class="grid gap-2 rounded-xl border border-dashed p-3 transition-colors"
    :class="dragging ? 'border-accent bg-accent/5' : 'border-line-soft'"
    data-attachments
    @dragover.prevent="dragging = true"
    @dragleave="dragging = false"
    @drop.prevent="onDrop"
  >
    <p class="flex items-center gap-2 text-[12.5px] font-semibold text-muted">
      <Icon name="clip" :size="14" /> {{ t('attach.title') }}
    </p>

    <ul v-if="shown.length" class="grid grid-cols-[repeat(auto-fill,minmax(7rem,1fr))] gap-2">
      <li v-for="item in shown" :key="item.key" class="group relative" :data-attachment="item.name">
        <button
          type="button"
          class="grid w-full gap-1 overflow-hidden rounded-lg border border-line-soft bg-raised/60 p-1.5 text-left"
          :aria-label="t('attach.open', { name: item.name })"
          @click="open(item)"
        >
          <img v-if="item.url" :src="item.url" alt="" class="aspect-square w-full rounded-md object-cover" />
          <span v-else class="grid aspect-square w-full place-items-center rounded-md bg-raised">
            <Icon name="file" :size="28" class="text-muted" />
          </span>
          <span class="truncate text-[11.5px]">{{ item.name }}</span>
          <span class="text-[11px] text-faint">
            {{
              item.row && missing[item.row.id]
                ? t(`attach.${missing[item.row.id]}`)
                : item.row && waiting(item.row.id)
                  ? t('attach.queued')
                  : sizeLabel(item.size, state.settings.language)
            }}
          </span>
        </button>
        <button
          v-if="editable"
          type="button"
          class="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-bg/85 shadow"
          :aria-label="t('attach.remove', { name: item.name })"
          @click="drop(item)"
        >
          <Icon name="x" :size="13" />
        </button>
      </li>
    </ul>

    <div v-if="editable" class="flex flex-wrap gap-2">
      <label class="btn-soft relative cursor-pointer !py-1.5 !text-[12.5px]">
        <Icon name="plus" :size="14" /> {{ t('attach.add') }}
        <input type="file" class="sr-only" accept="image/*,application/pdf" multiple data-attach-input @change="onInput" />
      </label>
      <label v-if="phone" class="btn-soft relative cursor-pointer !py-1.5 !text-[12.5px]">
        <Icon name="camera" :size="14" /> {{ t('attach.photo') }}
        <input type="file" class="sr-only" accept="image/*" capture="environment" @change="onInput" />
      </label>
    </div>
    <p v-if="editable" class="text-[11.5px] text-faint">{{ t('attach.drop') }}</p>
    <p v-else-if="!shown.length" class="text-[11.5px] text-faint">{{ t('attach.none') }}</p>
    <p v-if="error" class="text-[12.5px] text-danger" role="alert">{{ error }}</p>
  </section>
</template>
